/**
 * Centralized image-variant config used by <ShopifyImage>.
 *
 * This is the single place to tune which widths Shopify's image CDN
 * (image_url: width:) generates for each context. Changing a width list
 * here updates every gallery / card / lightbox image in the app.
 */

export type ShopifyImageVariant =
  | "gallery"
  | "thumbnail"
  | "card"
  | "lightbox"
  | "lightbox-zoom";

export interface ShopifyImageVariantConfig {
  /** Width candidates that will be requested from Shopify's CDN. */
  widths: number[];
  /** Width used for the base `src` attribute. */
  defaultWidth: number;
  /** `sizes` attribute used when the caller doesn't pass one. */
  fallbackSizes: string;
  /** Default loading strategy unless `priority`/`eager` is set. */
  defaultLoading: "eager" | "lazy";
  /** Intrinsic width/height attributes to reserve layout space (avoid CLS). */
  intrinsic: { width: number; height: number };
  /** When true, render a single hi-res `src` without srcset (zoom view). */
  noSrcSet?: boolean;
}

export const SHOPIFY_IMAGE_VARIANTS: Record<
  ShopifyImageVariant,
  ShopifyImageVariantConfig
> = {
  gallery: {
    // Covers rendered widths from ~300px (mobile/portrait) up to ~900px
    // (desktop landscape). The browser picks the smallest width >= rendered
    // px × DPR, so this list lets it land on 720/900 for ~360–450px renders,
    // 1000–1200 for ~500–600px renders, and 1600–1800 only when truly large.
    widths: [400, 600, 800, 1000, 1200, 1400, 1600, 1800],
    defaultWidth: 800,
    fallbackSizes:
      "(max-width: 749px) 100vw, (max-width: 989px) 60vw, min(57vw, 760px)",
    defaultLoading: "lazy",
    intrinsic: { width: 1200, height: 1500 },
  },
  thumbnail: {
    // Thumbs render ~80–96px, so 180/240/320 covers 1×–3× DPR cleanly.
    widths: [120, 180, 240, 320],
    defaultWidth: 180,
    fallbackSizes: "(max-width: 749px) 72px, 90px",
    defaultLoading: "lazy",
    intrinsic: { width: 90, height: 90 },
  },
  card: {
    widths: [240, 360, 480, 600, 720, 900],
    defaultWidth: 480,
    fallbackSizes:
      "(max-width: 749px) 50vw, (max-width: 989px) 33vw, min(25vw, 360px)",
    defaultLoading: "lazy",
    intrinsic: { width: 720, height: 720 },
  },
  lightbox: {
    // Lightbox images are letterboxed inside max-w-[92vw]/max-h-[88vh].
    // For typical portrait product shots the rendered width is well under
    // viewport width, so we cap the candidate widths around 1400 and let
    // the hi-res overlay (loaded on zoom) handle close inspection.
    widths: [600, 800, 1000, 1200, 1400, 1600],
    defaultWidth: 1200,
    fallbackSizes: "(max-width: 989px) 92vw, min(70vw, 1400px)",
    defaultLoading: "eager",
    intrinsic: { width: 1400, height: 1400 },
  },
  "lightbox-zoom": {
    widths: [2400],
    defaultWidth: 2400,
    fallbackSizes: "100vw",
    defaultLoading: "eager",
    intrinsic: { width: 2400, height: 2400 },
    noSrcSet: true,
  },
};